import React, { useState } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const ProjectCard = ({ project, onImageClick, onBookService }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const handlePrevImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === 0 ? project?.images?.length - 1 : prev - 1
    );
  };

  const handleNextImage = (e) => {
    e?.stopPropagation();
    setCurrentImageIndex(prev => 
      prev === project?.images?.length - 1 ? 0 : prev + 1
    );
  };

  const handleCardClick = () => {
    onImageClick(project, currentImageIndex);
  };

  const handleBookService = (e) => {
    e?.stopPropagation();
    onBookService(project?.serviceType);
  };

  const handleShare = (e) => {
    e?.stopPropagation();
    if (navigator.share) {
      navigator.share({
        title: project?.title,
        text: project?.description,
        url: window.location?.href
      });
    } else {
      navigator.clipboard?.writeText(window.location?.href);
      // You could show a toast notification here
    }
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-elevation-2 hover:shadow-elevation-4 transition-all duration-300 cursor-pointer group overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleCardClick}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <Image
          src={project?.images?.[currentImageIndex]?.url}
          alt={project?.images?.[currentImageIndex]?.alt || project?.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        
        {/* Image Navigation */}
        {project?.images?.length > 1 && (
          <>
            <button
              onClick={handlePrevImage}
              className={`absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center transition-opacity duration-200 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={handleNextImage}
              className={`absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 text-white rounded-full flex items-center justify-center transition-opacity duration-200 ${
                isHovered ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </>
        )}

        {/* Image Indicators */}
        {project?.images?.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex space-x-1">
            {project?.images?.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                  index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}

        {/* Before/After Badge */}
        {project?.type === 'before-after' && (
          <div className="absolute top-2 left-2 bg-primary text-white px-2 py-1 rounded text-xs font-medium">
            Before & After
          </div>
        )}

        {/* Featured Badge */}
        {project?.featured && (
          <div className="absolute top-2 right-2 bg-accent text-white px-2 py-1 rounded text-xs font-medium flex items-center space-x-1">
            <Icon name="Star" size={12} />
            <span>Featured</span>
          </div>
        )}

        {/* Hover Overlay */}
        <div className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white/90 backdrop-blur-sm rounded-lg p-4 text-center max-w-xs mx-4">
              <h4 className="font-semibold text-text-primary mb-2">{project?.title}</h4>
              <p className="text-sm text-text-secondary mb-3 line-clamp-2">
                {project?.description}
              </p>
              <div className="flex items-center justify-center space-x-2 text-xs text-text-secondary">
                <Icon name="Calendar" size={12} />
                <span>{project?.completionDate}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Card Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="font-semibold text-text-primary mb-1 line-clamp-1">
              {project?.title}
            </h3>
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <span className="bg-muted px-2 py-1 rounded text-xs font-medium">
                {project?.serviceTypeLabel}
              </span>
              <span>•</span>
              <span>{project?.location}</span>
            </div>
          </div>
          <button
            onClick={handleShare}
            className="text-text-secondary hover:text-primary transition-colors duration-200"
          >
            <Icon name="Share2" size={16} />
          </button>
        </div>

        <p className="text-sm text-text-secondary mb-4 line-clamp-2">
          {project?.description}
        </p>

        {/* Project Stats */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4 text-xs text-text-secondary">
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={12} />
              <span>{project?.duration}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="MapPin" size={12} />
              <span>{project?.area}</span>
            </div>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Eye" size={12} color="var(--color-text-secondary)" />
            <span className="text-xs text-text-secondary">{project?.views}</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCardClick}
            iconName="ZoomIn"
            iconPosition="left"
            className="flex-1"
          >
            View Details
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleBookService}
            iconName="Calendar"
            iconPosition="left"
            className="flex-1"
          >
            Book Service
          </Button>
        </div>

        {/* Customer Testimonial */}
        {project?.testimonial && (
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-start space-x-2">
              <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                <Image
                  src={project?.testimonial?.customerAvatar}
                  alt={project?.testimonial?.customerName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex-1">
                <p className="text-xs text-text-secondary italic line-clamp-2">
                  "{project?.testimonial?.comment}"
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-xs font-medium text-text-primary">
                    {project?.testimonial?.customerName}
                  </span>
                  <div className="flex items-center space-x-1">
                    {[...Array(5)]?.map((_, i) => (
                      <Icon
                        key={i}
                        name="Star"
                        size={10}
                        color={i < project?.testimonial?.rating ? "var(--color-warning)" : "var(--color-border)"}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;